import { createJSONStorage } from 'zustand/middleware'

export interface StorageOptions<T> {
  getParams: (searchParams: URLSearchParams) => string[]
  fromParams: (searchParams: URLSearchParams) => Partial<T> | null
  toParams: (state: T) => Record<string, string>
}

export const createLocalAndUrlStorage = <T>(options: StorageOptions<T>) => {
  const { fromParams, toParams, getParams } = options

  const setUrlSearch = (searchParams: URLSearchParams) => {
    window.history.replaceState(null, '', `?${searchParams.toString()}`)
  }

  const getUrlSearch = () =>
    new URLSearchParams(window.location.search.slice(1))

  const clearUrlSearch = () => {
    const searchParams = getUrlSearch()
    getParams(searchParams).forEach((param) => {
      searchParams.delete(param)
    })
    return searchParams
  }

  const getLocalValue = (key: string): { state: T } | null => {
    const localValue = localStorage.getItem(key)
    if (localValue) return JSON.parse(localValue)
    return null
  }

  const getUrlSearchValue = (): Partial<T> | null => {
    const searchParams = new URLSearchParams(getUrlSearch())
    return fromParams(searchParams)
  }

  const setUrlSearchValue = (state: T) => {
    const searchParams = getUrlSearch()
    const params = toParams(state)
    if (params === null) return

    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value)
    })
    setUrlSearch(searchParams)
  }

  return createJSONStorage<T>(() => ({
    getItem: (key): string => {
      const searchValue = getUrlSearchValue()
      const localValue = getLocalValue(key)

      if (searchValue) {
        const stringified = JSON.stringify({ state: searchValue })
        localStorage.setItem(key, stringified)
        return stringified
      }

      if (localValue) {
        // Defer URL update to avoid side effects during React's render phase
        queueMicrotask(() => setUrlSearchValue(localValue.state))
        return JSON.stringify(localValue)
      }

      return ''
    },
    setItem: (key, value): void => {
      const parsedValue = JSON.parse(value)

      setUrlSearch(clearUrlSearch())
      setUrlSearchValue(parsedValue.state)
      localStorage.setItem(key, value)
    },
    removeItem: (key): void => {
      localStorage.delete(key)
      window.location.search = clearUrlSearch().toString()
    },
  }))
}
